/**
 * Browse Marketplace Page
 * Professional marketplace browsing experience with filters, search, and sorting
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from 'primereact/button';
import { Toast } from 'primereact/toast';
import { Skeleton } from 'primereact/skeleton';
import { InputText } from 'primereact/inputtext';
import { Dropdown } from 'primereact/dropdown';
import { Slider } from 'primereact/slider';
import { SelectButton } from 'primereact/selectbutton';
import { OverlayPanel } from 'primereact/overlaypanel';
import { MarketplaceSwitch, ListingCard } from './components';
import { marketplaceService, LOAN_PRODUCTS } from '../../services/marketplaceService';
import axiosInstance from '../../config/axios.config';
import ExpressInterestDialog from '../../components/marketplace/interests/ExpressInterestDialog';
import NavigationTabMenu from '../../components/common/NavigationTabMenu';

// Buy Leads section tabs
const BUY_LEADS_TABS = [
    { label: 'Discover Leads', icon: 'pi pi-search', route: '/app/marketplace' },
    { label: 'Enquired Leads', icon: 'pi pi-bookmark', route: '/app/marketplace/my-interests' },
    { label: 'Post My Lead', icon: 'pi pi-plus', route: '/app/marketplace/post' }
];

const Browse = () => {
    const navigate = useNavigate();
    const toast = useRef(null);
    const searchTimeoutRef = useRef(null);
    const commissionOp = useRef(null);

    // State
    const [loading, setLoading] = useState(true);
    const [loadingMore, setLoadingMore] = useState(false);
    const [listings, setListings] = useState([]);
    const [scope, setScope] = useState('public');
    const [totalRecords, setTotalRecords] = useState(0);
    const [currentPage, setCurrentPage] = useState(1);
    const [perPage] = useState(8);
    const [hasMore, setHasMore] = useState(false);

    // Filter states
    const [searchQuery, setSearchQuery] = useState('');
    const [debouncedSearchQuery, setDebouncedSearchQuery] = useState('');
    const [sortBy, setSortBy] = useState('newest');
    const [productType, setProductType] = useState(null);
    const [qualityGrade, setQualityGrade] = useState(null);
    const [finderFeeRange, setFinderFeeRange] = useState([0.5, 10]);
    const [viewMode, setViewMode] = useState('grid');

    // ── Dynamic location filters (cascading) ──
    // State / City sourced from the same /location masters that the
    // PostLead / PostOffer forms use — no hardcoded lists.
    const [state, setState] = useState(null);
    const [city, setCity] = useState('');
    const [stateOptionsRaw, setStateOptionsRaw] = useState([]);
    const [cityOptionsRaw, setCityOptionsRaw] = useState([]);
    const [loadingCities, setLoadingCities] = useState(false);

    const stateOptions = React.useMemo(
        () => [{ label: 'All States', value: null }, ...stateOptionsRaw],
        [stateOptionsRaw]
    );
    const cityOptions = React.useMemo(
        () => [{ label: 'All Cities', value: '' }, ...cityOptionsRaw],
        [cityOptionsRaw]
    );

    // Load states once on mount.
    useEffect(() => {
        let cancelled = false;
        axiosInstance.get('/location/states')
            .then((res) => { if (!cancelled && res?.data?.success) setStateOptionsRaw(res.data.data || []); })
            .catch((e) => console.error('Failed to load states:', e?.message || 'error'));
        return () => { cancelled = true; };
    }, []);

    // Cascading cities for the chosen state.
    const loadCities = useCallback(async (selectedState) => {
        if (!selectedState) { setCityOptionsRaw([]); return; }
        setLoadingCities(true);
        try {
            const res = await axiosInstance.get(`/location/cities/by-state/${encodeURIComponent(selectedState)}`);
            setCityOptionsRaw(res?.data?.success ? (res.data.data || []) : []);
        } catch {
            setCityOptionsRaw([]);
        } finally {
            setLoadingCities(false);
        }
    }, []);

    useEffect(() => {
        if (state) loadCities(state); else setCityOptionsRaw([]);
    }, [state, loadCities]);

    // Interest dialog state
    const [showInterestDialog, setShowInterestDialog] = useState(false);
    const [selectedListingForInterest, setSelectedListingForInterest] = useState(null);

    // Options
    const sortOptions = [
        { label: 'Newest First', value: 'newest' },
        { label: 'Oldest First', value: 'oldest' },
        { label: 'Highest Lead Commission', value: 'commission' },
        { label: 'Best Quality', value: 'quality_score' },
        { label: 'Most Views', value: 'views' },
        { label: 'Most Enquiries', value: 'interest' }
    ];

    const qualityOptions = [
        { label: 'All Grades', value: null },
        { label: 'Grade A - Premium', value: 'A' },
        { label: 'Grade B - High', value: 'B' },
        { label: 'Grade C - Standard', value: 'C' },
        { label: 'Grade D - Basic', value: 'D' },
        { label: 'Grade E - Entry', value: 'E' }
    ];

    const productOptions = [
        { label: 'All Products', value: null },
        ...Object.entries(LOAN_PRODUCTS).map(([key, config]) => ({
            label: config.label,
            value: key
        }))
    ];

    const viewOptions = [
        { icon: 'pi pi-th-large', value: 'grid' },
        { icon: 'pi pi-list', value: 'list' }
    ];

    // Mobile detection — drives mobile-only changes without touching desktop.
    const [isMobile, setIsMobile] = useState(
        () => typeof window !== 'undefined' && window.matchMedia('(max-width: 767px)').matches
    );
    useEffect(() => {
        const mq = window.matchMedia('(max-width: 767px)');
        const handler = (e) => setIsMobile(e.matches);
        mq.addEventListener('change', handler);
        return () => mq.removeEventListener('change', handler);
    }, []);

    // On mobile, hide the "Post My Lead" tab — the header "Post Lead" button
    // replaces it. Desktop keeps all tabs unchanged.
    const visibleTabs = isMobile
        ? BUY_LEADS_TABS.filter((t) => t.route !== '/app/marketplace/post')
        : BUY_LEADS_TABS;

    // Debounce search query
    useEffect(() => {
        // Clear existing timeout
        if (searchTimeoutRef.current) {
            clearTimeout(searchTimeoutRef.current);
        }

        // Set new timeout to update debounced search after 500ms
        searchTimeoutRef.current = setTimeout(() => {
            setDebouncedSearchQuery(searchQuery);
        }, 500);

        // Cleanup on unmount
        return () => {
            if (searchTimeoutRef.current) {
                clearTimeout(searchTimeoutRef.current);
            }
        };
    }, [searchQuery]);

    // Fetch listings (initial load - replaces existing data)
    const fetchListings = useCallback(async () => {
        setLoading(true);
        try {
            const filters = {
                scope,
                exclude_own: true,  // Don't show user's own listings in browse
                sort_by: sortBy,
                per_page: perPage,
                page: 1 // Always start from page 1 for initial load
            };

            // Add search query to backend filters
            if (debouncedSearchQuery) {
                filters.search = debouncedSearchQuery;
            }

            if (productType) filters.loan_type = productType;
            if (qualityGrade) filters.quality_grade = qualityGrade;
            if (state) filters.state = state;
            if (city) filters.city = city;
            if (finderFeeRange[0] > 0.5) filters.commission_min = finderFeeRange[0];
            if (finderFeeRange[1] < 10) filters.commission_max = finderFeeRange[1];

            console.log('Fetching with filters:', filters);

            const response = await marketplaceService.browse(filters);

            if (response.success) {
                let listingsData = [];
                let total = 0;
                const data = response.data;

                if (data) {
                    if (data.data && Array.isArray(data.data)) {
                        listingsData = data.data;
                        total = data.total || data.data.length;
                    } else if (data.listings && Array.isArray(data.listings)) {
                        listingsData = data.listings;
                        total = data.total || data.listings.length;
                    } else if (Array.isArray(data)) {
                        listingsData = data;
                        total = data.length;
                    }
                }

                console.log('Fetched listings:', listingsData.length, 'Total:', total);

                // Replace listings (initial load)
                setListings(listingsData);
                setTotalRecords(total);
                setCurrentPage(1);

                // Check if there are more results
                setHasMore(listingsData.length < total);
            }
        } catch (error) {
            console.error('Failed to fetch listings:', error);
            toast.current?.show({
                severity: 'error',
                summary: 'Error',
                detail: 'Failed to load marketplace listings'
            });
        } finally {
            setLoading(false);
        }
    }, [scope, sortBy, productType, qualityGrade, state, city, finderFeeRange, perPage, debouncedSearchQuery]);

    // Fetch listings when dependencies change (filters, search, etc.)
    useEffect(() => {
        fetchListings();
    }, [fetchListings]);

    // Load more listings (append to existing)
    const loadMore = async () => {
        if (loadingMore || !hasMore) return;

        setLoadingMore(true);
        try {
            const nextPage = currentPage + 1;
            const filters = {
                scope,
                exclude_own: true,  // Don't show user's own listings in browse
                sort_by: sortBy,
                per_page: perPage,
                page: nextPage
            };

            // Add search query to backend filters
            if (debouncedSearchQuery) {
                filters.search = debouncedSearchQuery;
            }

            if (productType) filters.loan_type = productType;
            if (qualityGrade) filters.quality_grade = qualityGrade;
            if (state) filters.state = state;
            if (city) filters.city = city;
            if (finderFeeRange[0] > 0.5) filters.commission_min = finderFeeRange[0];
            if (finderFeeRange[1] < 10) filters.commission_max = finderFeeRange[1];

            console.log('Loading more with filters:', filters);

            const response = await marketplaceService.browse(filters);

            if (response.success) {
                let listingsData = [];
                let total = 0;
                const data = response.data;

                if (data) {
                    if (data.data && Array.isArray(data.data)) {
                        listingsData = data.data;
                        total = data.total || data.data.length;
                    } else if (data.listings && Array.isArray(data.listings)) {
                        listingsData = data.listings;
                        total = data.total || data.listings.length;
                    } else if (Array.isArray(data)) {
                        listingsData = data;
                        total = data.length;
                    }
                }

                console.log('Loaded more listings:', listingsData.length);

                // Append new listings to existing
                setListings(prev => [...prev, ...listingsData]);
                setCurrentPage(nextPage);
                setTotalRecords(total);

                // Check if there are more results
                const totalLoaded = listings.length + listingsData.length;
                setHasMore(totalLoaded < total);
            }
        } catch (error) {
            console.error('Failed to load more listings:', error);
            toast.current?.show({
                severity: 'error',
                summary: 'Error',
                detail: 'Failed to load more listings'
            });
        } finally {
            setLoadingMore(false);
        }
    };

    const clearFilters = () => {
        setSearchQuery('');
        setDebouncedSearchQuery('');
        setSortBy('newest');
        setProductType(null);
        setQualityGrade(null);
        setState(null);
        setCity('');
        setFinderFeeRange([0.5, 10]);
    };

    const activeFiltersCount = [
        debouncedSearchQuery,
        productType,
        qualityGrade,
        state,
        city,
        finderFeeRange[0] > 0.5 || finderFeeRange[1] < 10
    ].filter(Boolean).length;

    // Handle interest success
    const handleInterestSuccess = () => {
        setShowInterestDialog(false);
        setSelectedListingForInterest(null);
        toast.current?.show({
            severity: 'success',
            summary: 'Enquiry Sent!',
            detail: 'The owner will review your proposal'
        });
        // Optionally refresh listings to update interest count
        fetchListings();
    };

    // Prepare lead data for ExpressInterestDialog
    const getLeadDataForDialog = (listing) => {
        if (!listing) return null;

        // Get borrower name
        const borrowerName = listing.borrower_preview?.name ||
            listing.lead_snapshot?.borrower_name ||
            listing.lead_snapshot?.masked_name ||
            'Unknown';

        // Get loan amount
        const loanAmount = listing.lead_details?.loan_amount_required ||
            listing.lead_snapshot?.loan_amount_required ||
            listing.lead_snapshot?.loan_amount ||
            0;

        // Get product type
        const productType = listing.lead_details?.product_type ||
            listing.lead_snapshot?.product_type ||
            'personal_loan';

        // Get quality grade
        const qualityGrade = listing.quality?.grade ||
            listing.quality_grade ||
            'C';

        // Get finder fee percentage
        const commissionPercentage = listing.collaboration?.commission_percentage ||
            listing.collaboration_commission_percentage ||
            3.0;

        // Get seller name
        const sellerName = listing.seller?.full_name ||
            listing.seller?.display_name ||
            listing.posted_by_name ||
            'Seller';

        return {
            id: listing.id,
            borrower_name: borrowerName,
            loan_amount: loanAmount,
            product_type: productType,
            quality_grade: qualityGrade,
            commission_percentage: commissionPercentage,
            seller_name: sellerName
        };
    };

    // List Header for table-like view
    const ListHeader = () => (
        <div className="surface-100 border-bottom-1 surface-border px-3 py-2 hidden md:flex">
            <div className="flex align-items-center font-semibold text-500 uppercase" style={{ fontSize: '0.65rem' }}>
                <div style={{ width: '32px' }} className="mr-3"></div>
                <div style={{ width: '200px' }}>Borrower</div>
                <div style={{ width: '100px' }} className="text-right">Amount</div>
                <div style={{ width: '150px' }} className="px-3">Location</div>
                <div style={{ width: '80px' }} className="text-center">Commission</div>
                <div style={{ width: '60px' }} className="text-center hidden lg:block">Spots</div>
                <div className="ml-auto" style={{ width: '180px' }}></div>
            </div>
        </div>
    );

    // Loading skeleton
    const renderSkeleton = () => (
        <div className="grid">
            {[1, 2, 3, 4, 5, 6, 7, 8].map(i => (
                <div key={i} className={viewMode === 'grid' ? 'col-12 md:col-6 lg:col-4 xl:col-3 p-2' : 'col-12'}>
                    <div className="surface-card border-round border-1 surface-border p-2">
                        <div className="flex align-items-start justify-content-between mb-2">
                            <Skeleton width="80px" height="1.2rem" />
                            <Skeleton shape="circle" size="24px" />
                        </div>
                        <Skeleton width="70%" height="0.85rem" className="mb-1" />
                        <Skeleton width="50%" height="1rem" className="mb-2" />
                        <Skeleton width="100%" height="40px" className="mb-1" />
                        <div className="flex justify-content-between">
                            <Skeleton width="50px" height="0.75rem" />
                            <Skeleton width="70px" height="0.75rem" />
                        </div>
                    </div>
                </div>
            ))}
        </div>
    );

    // Empty state
    const renderEmpty = () => (
        <div className="flex flex-column align-items-center justify-content-center py-5">
            <div className="bg-primary-50 border-circle p-4 mb-3">
                <i className="pi pi-search text-primary text-3xl" />
            </div>
            <h3 className="text-900 font-semibold mb-1" style={{ fontSize: '0.95rem' }}>No Listings Found</h3>
            <p className="text-600 text-center mb-3 line-height-3" style={{ maxWidth: '350px', fontSize: '0.75rem' }}>
                {debouncedSearchQuery || activeFiltersCount > 0
                    ? 'Try adjusting your filters or search terms to find more leads.'
                    : scope === 'network'
                        ? 'No leads available in your network yet. Expand your connections!'
                        : 'No leads available in the public marketplace right now.'}
            </p>
            <div className="flex gap-1">
                {(debouncedSearchQuery || activeFiltersCount > 0) && (
                    <Button
                        label="Clear Filters"
                        icon="pi pi-filter-slash"
                        className="p-button-outlined"
                        style={{ fontSize: '0.7rem', padding: '0.35rem 0.6rem' }}
                        onClick={clearFilters}
                    />
                )}
                <Button
                    label="Post Your Lead"
                    icon="pi pi-plus"
                    style={{ fontSize: '0.7rem', padding: '0.35rem 0.6rem' }}
                    onClick={() => navigate('/app/marketplace/post')}
                />
            </div>
        </div>
    );

    return (
        <div className="marketplace-browse">
            <NavigationTabMenu tabs={visibleTabs} />
            <Toast ref={toast} />

            {/* Compact Header */}
            <div className="surface-card border-round shadow-1 p-2 mb-2">
                {/* Row 1: Title + Actions */}
                {isMobile ? (
                    /* Mobile: no title — two equal, full-tap-size action buttons */
                    <div className="flex gap-2 mb-2">
                        <Button
                            icon="pi pi-plus"
                            label="Post Lead"
                            className="p-button-sm flex-1"
                            onClick={() => navigate('/app/marketplace/post')}
                        />
                        <Button
                            icon="pi pi-list"
                            label="My Listings"
                            className="p-button-outlined p-button-sm flex-1"
                            onClick={() => navigate('/app/marketplace/my-listings')}
                        />
                    </div>
                ) : (
                    /* Desktop: original title + actions (unchanged) */
                    <div className="flex align-items-center justify-content-between mb-2">
                        <h2 className="text-900 font-bold m-0" style={{ fontSize: '1rem' }}>Discover Leads</h2>
                        <div className="flex align-items-center gap-1">
                            <Button
                                icon="pi pi-list"
                                label="My Listings"
                                className="p-button-outlined"
                                style={{ fontSize: '0.7rem', padding: '0.35rem 0.6rem' }}
                                onClick={() => navigate('/app/marketplace/my-listings')}
                            />
                            <Button
                                icon="pi pi-plus"
                                label="Post Lead"
                                style={{ fontSize: '0.7rem', padding: '0.35rem 0.6rem' }}
                                onClick={() => navigate('/app/marketplace/post')}
                            />
                        </div>
                    </div>
                )}
                {/* Row 2: Scope Switcher + Refresh */}
                <div className="flex align-items-center gap-2">
                    {/* Mobile: Compact full width switcher */}
                    <div className="flex-1 md:hidden">
                        <MarketplaceSwitch
                            value={scope}
                            onChange={setScope}
                            fullWidth
                            compact
                        />
                    </div>
                    {/* Desktop: Compact switcher */}
                    <div className="hidden md:block">
                        <MarketplaceSwitch
                            value={scope}
                            onChange={setScope}
                            compact
                        />
                    </div>
                    <Button
                        icon="pi pi-refresh"
                        className="p-button-text p-button-rounded flex-shrink-0"
                        onClick={fetchListings}
                        loading={loading}
                        tooltip="Refresh"
                        tooltipOptions={{ position: 'top' }}
                        style={{ width: '1.75rem', height: '1.75rem' }}
                    />
                </div>
            </div>

            {/* Search and Filter Bar - Compact */}
            <div className="surface-card border-round shadow-1 p-2 mb-2">
                {/* Mobile: Search on own row, then filters 2 per row */}
                <div className="md:hidden">
                    <div className="mb-2">
                        <span className="p-input-icon-left p-input-icon-right w-full">
                            <i className="pi pi-search" style={{ left: '0.75rem', fontSize: '0.7rem' }} />
                            <InputText
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                placeholder="Search..."
                                className="w-full p-inputtext-sm"
                                style={{ fontSize: '0.7rem', paddingLeft: '2rem', paddingRight: '2rem' }}
                            />
                            {searchQuery && searchQuery !== debouncedSearchQuery && (
                                <i className="pi pi-spin pi-spinner" style={{ right: '0.75rem', fontSize: '0.7rem' }} />
                            )}
                        </span>
                    </div>
                    <div className="grid m-0">
                        <div className="col-6 p-1">
                            <Dropdown
                                value={sortBy}
                                options={sortOptions}
                                onChange={(e) => setSortBy(e.value)}
                                placeholder="Sort"
                                className="w-full p-inputtext-sm"
                                panelStyle={{ fontSize: '0.7rem' }}
                                pt={{ input: { style: { fontSize: '0.7rem' } }, item: { style: { fontSize: '0.7rem' } } }}
                            />
                        </div>
                        <div className="col-6 p-1">
                            <Dropdown
                                value={productType}
                                options={productOptions}
                                onChange={(e) => setProductType(e.value)}
                                placeholder="Product"
                                className="w-full p-inputtext-sm"
                                panelStyle={{ fontSize: '0.7rem' }}
                                pt={{ input: { style: { fontSize: '0.7rem' } }, item: { style: { fontSize: '0.7rem' } } }}
                            />
                        </div>
                        <div className="col-6 p-1">
                            <Dropdown
                                value={qualityGrade}
                                options={qualityOptions}
                                onChange={(e) => setQualityGrade(e.value)}
                                placeholder="Grade"
                                className="w-full p-inputtext-sm"
                                panelStyle={{ fontSize: '0.7rem' }}
                                pt={{ input: { style: { fontSize: '0.7rem' } }, item: { style: { fontSize: '0.7rem' } } }}
                            />
                        </div>
                        <div className="col-6 p-1">
                            <Dropdown
                                value={state}
                                options={stateOptions}
                                onChange={(e) => { setState(e.value); setCity(''); }}
                                optionLabel="label"
                                optionValue="value"
                                placeholder="State"
                                filter
                                showClear
                                className="w-full p-inputtext-sm"
                                panelStyle={{ fontSize: '0.7rem' }}
                                pt={{ input: { style: { fontSize: '0.7rem' } }, item: { style: { fontSize: '0.7rem' } } }}
                            />
                        </div>
                        <div className="col-6 p-1">
                            <Dropdown
                                value={city}
                                options={cityOptions}
                                onChange={(e) => setCity(e.value || '')}
                                optionLabel="label"
                                optionValue="value"
                                placeholder={state ? 'City' : 'Pick state'}
                                disabled={!state || loadingCities}
                                filter
                                showClear
                                className="w-full p-inputtext-sm"
                                panelStyle={{ fontSize: '0.7rem' }}
                                pt={{ input: { style: { fontSize: '0.7rem' } }, item: { style: { fontSize: '0.7rem' } } }}
                            />
                        </div>
                        <div className="col-6 p-1">
                            <Button
                                type="button"
                                label={finderFeeRange[0] > 0.5 || finderFeeRange[1] < 10
                                    ? `${finderFeeRange[0].toFixed(1)}-${finderFeeRange[1].toFixed(1)}%`
                                    : 'Commission'}
                                icon="pi pi-percentage"
                                className={`w-full p-button-outlined ${finderFeeRange[0] > 0.5 || finderFeeRange[1] < 10 ? 'p-button-warning' : ''}`}
                                style={{ fontSize: '0.7rem', padding: '0.35rem 0.5rem' }}
                                onClick={(e) => commissionOp.current.toggle(e)}
                            />
                        </div>
                        {activeFiltersCount > 0 && (
                            <div className="col-12 p-1">
                                <Button
                                    icon="pi pi-filter-slash"
                                    label="Clear filters"
                                    className="p-button-text w-full"
                                    style={{ fontSize: '0.65rem', padding: '0.25rem 0.5rem' }}
                                    onClick={clearFilters}
                                />
                            </div>
                        )}
                    </div>
                </div>

                {/* Desktop: Filters in labeled row like DSA Offers */}
                <div className="hidden md:flex align-items-end gap-2 flex-wrap">
                    <div className="flex-1" style={{ minWidth: '140px', maxWidth: '220px' }}>
                        <label className="block text-500 mb-1" style={{ fontSize: '0.86rem' }}>Search</label>
                        <span className="p-input-icon-left p-input-icon-right w-full">
                            <i className="pi pi-search" style={{ fontSize: '0.7rem' }} />
                            <InputText
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                placeholder="Name, location, code..."
                                className="w-full p-inputtext-sm"
                                style={{ fontSize: '0.7rem', paddingLeft: '2rem' }}
                            />
                            {searchQuery && searchQuery !== debouncedSearchQuery && (
                                <i className="pi pi-spin pi-spinner" style={{ right: '0.75rem', fontSize: '0.7rem' }} />
                            )}
                        </span>
                    </div>
                    <div className="flex-1" style={{ minWidth: '100px', maxWidth: '140px' }}>
                        <label className="block text-500 mb-1" style={{ fontSize: '0.86rem' }}>Sort By</label>
                        <Dropdown
                            value={sortBy}
                            options={sortOptions}
                            onChange={(e) => setSortBy(e.value)}
                            placeholder="Sort"
                            className="w-full p-inputtext-sm"
                            panelStyle={{ fontSize: '0.7rem' }}
                            pt={{ input: { style: { fontSize: '0.7rem' } }, item: { style: { fontSize: '0.7rem' } } }}
                        />
                    </div>
                    <div className="flex-1" style={{ minWidth: '100px', maxWidth: '130px' }}>
                        <label className="block text-500 mb-1" style={{ fontSize: '0.86rem' }}>Product</label>
                        <Dropdown
                            value={productType}
                            options={productOptions}
                            onChange={(e) => setProductType(e.value)}
                            placeholder="All Products"
                            showClear
                            className="w-full p-inputtext-sm"
                            panelStyle={{ fontSize: '0.7rem' }}
                            pt={{ input: { style: { fontSize: '0.7rem' } }, item: { style: { fontSize: '0.7rem' } } }}
                        />
                    </div>
                    <div className="flex-1" style={{ minWidth: '90px', maxWidth: '120px' }}>
                        <label className="block text-500 mb-1" style={{ fontSize: '0.86rem' }}>Grade</label>
                        <Dropdown
                            value={qualityGrade}
                            options={qualityOptions}
                            onChange={(e) => setQualityGrade(e.value)}
                            placeholder="All Grades"
                            showClear
                            className="w-full p-inputtext-sm"
                            panelStyle={{ fontSize: '0.7rem' }}
                            pt={{ input: { style: { fontSize: '0.7rem' } }, item: { style: { fontSize: '0.7rem' } } }}
                        />
                    </div>
                    {/* Cascading State + City — dynamic from /location masters */}
                    <div className="flex-1" style={{ minWidth: '100px', maxWidth: '130px' }}>
                        <label className="block text-500 mb-1" style={{ fontSize: '0.86rem' }}>State</label>
                        <Dropdown
                            value={state}
                            options={stateOptions}
                            onChange={(e) => { setState(e.value); setCity(''); }}
                            optionLabel="label"
                            optionValue="value"
                            placeholder="State"
                            filter
                            showClear
                            className="w-full p-inputtext-sm"
                            panelStyle={{ fontSize: '0.7rem' }}
                            pt={{ input: { style: { fontSize: '0.7rem' } }, item: { style: { fontSize: '0.7rem' } } }}
                        />
                    </div>
                    <div className="flex-1" style={{ minWidth: '100px', maxWidth: '130px' }}>
                        <label className="block text-500 mb-1" style={{ fontSize: '0.86rem' }}>City</label>
                        <Dropdown
                            value={city}
                            options={cityOptions}
                            onChange={(e) => setCity(e.value || '')}
                            optionLabel="label"
                            optionValue="value"
                            placeholder={loadingCities ? 'Loading…' : (state ? 'City' : 'Pick state first')}
                            disabled={!state || loadingCities}
                            filter
                            showClear
                            emptyMessage="No cities"
                            className="w-full p-inputtext-sm"
                            panelStyle={{ fontSize: '0.7rem' }}
                            pt={{ input: { style: { fontSize: '0.7rem' } }, item: { style: { fontSize: '0.7rem' } } }}
                        />
                    </div>
                    <div className="flex-shrink-0">
                        <label className="block text-500 mb-1" style={{ fontSize: '0.86rem' }}>Commission</label>
                        <Button
                            type="button"
                            label={finderFeeRange[0] > 0.5 || finderFeeRange[1] < 10
                                ? `${finderFeeRange[0].toFixed(1)}-${finderFeeRange[1].toFixed(1)}%`
                                : 'Range'}
                            icon="pi pi-percentage"
                            className={`p-button-outlined ${finderFeeRange[0] > 0.5 || finderFeeRange[1] < 10 ? 'p-button-warning' : ''}`}
                            style={{ fontSize: '0.7rem', padding: '0.35rem 0.5rem' }}
                            onClick={(e) => commissionOp.current.toggle(e)}
                        />
                    </div>
                    {/* View Toggle - Right aligned */}
                    <div className="hidden md:flex align-items-center gap-1 ml-auto flex-shrink-0">
                        <SelectButton
                            value={viewMode}
                            options={viewOptions}
                            onChange={(e) => e.value && setViewMode(e.value)}
                            optionLabel="icon"
                            itemTemplate={(option) => <i className={option.icon} style={{ fontSize: '0.7rem' }} />}
                            style={{ transform: 'scale(0.85)' }}
                        />
                    </div>
                </div>

                {/* Active filters & Results count */}
                <div className="flex align-items-center justify-content-between mt-2 pt-2 border-top-1 surface-border">
                    <span className="text-500" style={{ fontSize: '0.7rem' }}>
                        {loading ? 'Loading...' : `${totalRecords} listing${totalRecords !== 1 ? 's' : ''}`}
                        {activeFiltersCount > 0 && ` \u2022 ${activeFiltersCount} filter${activeFiltersCount > 1 ? 's' : ''}`}
                    </span>
                    {activeFiltersCount > 0 && (
                        <Button
                            label="Clear"
                            icon="pi pi-filter-slash"
                            className="p-button-text"
                            style={{ fontSize: '0.7rem', padding: '0.25rem 0.5rem' }}
                            onClick={clearFilters}
                        />
                    )}
                </div>
            </div>

            {/* Commission Overlay Panel */}
            <OverlayPanel
                ref={commissionOp}
                id="commission_overlay"
                showCloseIcon
                className="w-16rem"
            >
                <div className="p-2">
                    <div className="font-semibold text-900 mb-2" style={{ fontSize: '0.8rem' }}>Commission Range</div>
                    <div className="text-600 mb-2" style={{ fontSize: '0.7rem' }}>
                        Filter by commission percentage.
                    </div>
                    <div className="flex justify-content-between mb-2" style={{ fontSize: '0.7rem' }}>
                        <span className="text-600">Min: {finderFeeRange[0].toFixed(1)}%</span>
                        <span className="text-600">Max: {finderFeeRange[1].toFixed(1)}%</span>
                    </div>
                    <Slider
                        value={finderFeeRange}
                        onChange={(e) => setFinderFeeRange(e.value)}
                        range
                        min={0.5}
                        max={10}
                        step={0.1}
                        className="mb-2"
                    />
                    <div className="flex justify-content-between align-items-center mt-2">
                        <Button
                            label="Reset"
                            className="p-button-text"
                            style={{ fontSize: '0.7rem', padding: '0.25rem 0.5rem' }}
                            onClick={() => setFinderFeeRange([0.5, 10])}
                        />
                        <Button
                            label="Apply"
                            style={{ fontSize: '0.7rem', padding: '0.25rem 0.5rem' }}
                            onClick={(e) => commissionOp.current.hide()}
                        />
                    </div>
                </div>
            </OverlayPanel>

            {/* Listings */}
            {loading ? renderSkeleton() : listings.length === 0 ? renderEmpty() : (
                <>
                    {viewMode === 'list' ? (
                        // List View - Table-like with header
                        <div className="surface-card border-round border-1 surface-border overflow-hidden">
                            <ListHeader />
                            {listings.map(listing => (
                                <ListingCard
                                    key={listing.id}
                                    listing={listing}
                                    viewMode="list"
                                    onView={() => navigate(`/app/marketplace/listing/${listing.id}`)}
                                    onInterest={() => {
                                        setSelectedListingForInterest(listing);
                                        setShowInterestDialog(true);
                                    }}
                                />
                            ))}
                        </div>
                    ) : (
                        // Grid View
                        <div className="grid">
                            {listings.map(listing => (
                                <div key={listing.id} className="col-12 md:col-6 lg:col-4 xl:col-3 p-2">
                                    <ListingCard
                                        listing={listing}
                                        viewMode="grid"
                                        onView={() => navigate(`/app/marketplace/listing/${listing.id}`)}
                                        onInterest={() => {
                                            setSelectedListingForInterest(listing);
                                            setShowInterestDialog(true);
                                        }}
                                    />
                                </div>
                            ))}
                        </div>
                    )}

                    {/* Show More Button */}
                    {hasMore && (
                        <div className="flex flex-column align-items-center mt-3 mb-2">
                            <Button
                                label={loadingMore ? 'Loading...' : 'Show More'}
                                icon={loadingMore ? 'pi pi-spin pi-spinner' : 'pi pi-chevron-down'}
                                iconPos="right"
                                onClick={loadMore}
                                loading={loadingMore}
                                disabled={loadingMore}
                                className="p-button-outlined"
                                style={{ fontSize: '0.75rem', padding: '0.4rem 1.5rem' }}
                            />
                            <div className="text-center mt-2">
                                <span className="text-600" style={{ fontSize: '0.7rem' }}>
                                    Showing {listings.length} of {totalRecords} leads
                                </span>
                            </div>
                        </div>
                    )}

                    {/* All Loaded Message */}
                    {!hasMore && listings.length > 0 && (
                        <div className="flex align-items-center justify-content-center mt-3 mb-2 p-2 surface-100 border-round">
                            <i className="pi pi-check-circle text-green-500 mr-2" style={{ fontSize: '1rem' }} />
                            <span className="text-700 font-medium" style={{ fontSize: '0.75rem' }}>All {totalRecords} listings viewed</span>
                        </div>
                    )}
                </>
            )}

            {/* Express Interest Dialog */}
            <ExpressInterestDialog
                visible={showInterestDialog}
                onHide={() => {
                    setShowInterestDialog(false);
                    setSelectedListingForInterest(null);
                }}
                lead={getLeadDataForDialog(selectedListingForInterest)}
                suggestedPercentage={selectedListingForInterest?.collaboration?.commission_percentage || selectedListingForInterest?.collaboration_commission_percentage || 3.0}
                onSuccess={handleInterestSuccess}
            />
        </div>
    );
};

export default Browse;
